import {create} from 'zustand';
import {getUserPermissions, Permission} from "../clients/metoro/rbac";

const STORAGE_KEY = 'metoro-permissions';
const STORAGE_KEY_LAST_LOAD = 'metoro-permissions-last-load';

interface PermissionsStore {
    // The unix timestamp in seconds of the last load
    lastLoad: number;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
    loadPermissions: () => Promise<Permission[]>;
    hasPermissions: (resourceName: string, operation: string) => Promise<boolean>;
}


// Try to get config from localStorage
const getStoredPermissions = (): Permission[] | null => {
    try {
        const lastLoad = localStorage.getItem(STORAGE_KEY_LAST_LOAD)
        //
        if (!lastLoad || (new Date().getTime() / 1000 - Number(lastLoad) > 300)) {
            return null;
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error('Failed to parse stored config:', error);
    }
    return null
};

function doesPermissionMatch(permission: Permission, operation: string) {
    const operations = permission.operation.split(',').map(op => op.trim());

    // Check if the requested operation exists in the permitted operations
    return operations.includes(operation);
}

function doesResourceMatch(x: Permission, resourceName: string): boolean {
    // If the permission resource is "*", it matches everything
    if (x.resource === "*") {
        return true;
    }

    // Split both resources into parts
    const permParts = x.resource.split("/");
    const reqParts = resourceName.split("/");

    // If permission has more parts than request, it can't match
    if (permParts.length > reqParts.length) {
        return false;
    }

    // First, check if the permission is for the correct resource type
    if (permParts.length > 0 && permParts[0] !== reqParts[0]) {
        return false;
    }

    // Check each part
    for (let i = 0; i < permParts.length; i++) {
        const permPart = permParts[i];
        // If this part is "*", it matches anything at this level
        if (permPart === "*") {
            // If this is the last part of the permission, it matches all remaining parts of the request
            if (i === permParts.length - 1) {
                return true;
            }
            continue;
        }
        // If parts don't match exactly, permission denied
        if (permPart !== reqParts[i]) {
            return false;
        }
    }

    // If we've matched all parts of the permission, and the lengths are equal, it's a match
    return permParts.length === reqParts.length;
}

function hasPermissionsFrontend(permissions: Permission[], resourceName: string, operation: string): boolean {
    return permissions.map(x => doesPermissionMatch(x, operation) && doesResourceMatch(x, resourceName)).reduce((x, y) => x || y, false)
}

export const usePermissionsStore = create<PermissionsStore>((set) => {
    const loadPermissions = async () => {
        // If we already have a stored config, use it initially
        const storedPermissions = getStoredPermissions();
        if (storedPermissions) {
            return storedPermissions
        }

        set({isLoading: true, error: null});
        try {
            const permissions = await getUserPermissions()

            // Store in localStorage
            localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions));
            localStorage.setItem(STORAGE_KEY_LAST_LOAD, (new Date().getTime() / 1000).toString());

            set({
                isLoading: false,
                isInitialized: true
            });
            return permissions;
        } catch (error) {
            console.error('Failed to load Metoro configuration:', error);
            set({
                error: 'Failed to load configuration',
                isLoading: false,
                isInitialized: true,
                // If we have stored config, keep using it on error
            });
        }
        return []
    }

    return {
        isLoading: false,
        isInitialized: false,
        error: null,
        lastLoad: 0,
        loadPermissions: loadPermissions,
        hasPermissions: async (resourceName: string, operation: string) => hasPermissionsFrontend(await loadPermissions(), resourceName, operation)
    }
});